import React, { useMemo } from 'react';
import { Form, Card, Input } from 'antd';
import type { FormInstance } from 'antd';
import getInputByDisplayType, { Field, VariableOption } from './componentType';
import getVariableComponent, { Variable } from './variableType';
import './index.scss';
import { NODE_TYPE } from '../../constants';
import BranchEditor from './branch';
import atoms from '@/styles/atoms.module.scss';

interface Node {
  id: number;
  name: string;
  description: string;
  icon: string;
  fields: Field[];
  variables?: Variable[];
}

interface NodeFormProps {
  node: Node;
  form: FormInstance;
  upstreamNodes?: VariableOption[]; // 上游节点信息
  availableTargetNodes?: { label: string; value: string }[]; // 添加这一行
}

const NodeForm: React.FC<NodeFormProps> = ({ node, form, upstreamNodes, availableTargetNodes }) => {
  const inputVariables = node.variables?.filter(i => i.type === 1) || [];
  const outputVariables = node.variables?.filter(i => i.type === 2) || [];

  const upstreamVariables = useMemo<VariableOption[]>(() => {
    // 直接返回传入的upstreamNodes，如果为空则返回空数组
    return upstreamNodes && Array.isArray(upstreamNodes) ? upstreamNodes : [];
  }, [upstreamNodes]);

  return (
    <Card className="mb-4 rounded-2xl shadow-sm" style={{ border: '1px solid var(--color-border)' }}>
      <Form
        form={form}
        layout="vertical"
        initialValues={Object.fromEntries(
          node.fields?.map(f => [
            f.fieldName,
            f.defaultValue ? (f.fieldType == 'boolean' ? JSON.parse(f.defaultValue) : f.defaultValue) : null,
          ])
        )}
      >
        <Form.Item name="label" label="节点名称" rules={[{ required: true, message: `节点名称为必填项` }]}>
          <Input />
        </Form.Item>
        {node.fields?.map(field => (
          <Form.Item
            key={field.fieldName}
            name={field.fieldName}
            label={
              <span className="flex items-center gap-1">
                {field.description}
                {field.acceptVariable && <span className={`text-xs ${atoms.textPrimary}`}>(可引用变量)</span>}
              </span>
            }
            rules={[{ required: field.required, message: `${field.fieldLabel}为必填项` }]}
            valuePropName={field.displayType === 'switch' ? 'checked' : 'value'}
            tooltip={field.description}
          >
            {getInputByDisplayType(field, upstreamVariables)}
          </Form.Item>
        ))}
        {node.id === NODE_TYPE.CONDITION && (
          // <Form.Item name="branches" valuePropName="dataSource">
          <BranchEditor form={form} upstreamVariables={upstreamVariables} availableTargetNodes={availableTargetNodes} />
          // </Form.Item>
        )}

        {inputVariables.length > 0 || outputVariables.length > 0 ? <div className="bg-divider-subtle"></div> : null}
        {inputVariables.length > 0 && (
          <>
            <div className={`h-6 cursor-pointer items-center font-medium ${atoms.textSecondary}`}>输入变量</div>
            {inputVariables.map(variable => getVariableComponent(variable))}
          </>
        )}
        {outputVariables.length > 0 && (
          <>
            <div className={`h-6 cursor-pointer items-center font-medium ${atoms.textSecondary}`}>输出变量</div>
            {outputVariables.map(variable => getVariableComponent(variable))}
          </>
        )}
      </Form>
    </Card>
  );
};

export default NodeForm;
